import { useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useDataProvider } from "ra-core";
import type { CrmDataProvider } from "../providers/types";
import type {
  DurableEchoLedgerProjection,
  DurableRecoveryAction,
} from "./durableEchoLedger";

type Props = {
  workloadId: string;
  projection: DurableEchoLedgerProjection;
  actions: DurableRecoveryAction[];
  onSubmitted: (action: DurableRecoveryAction) => void;
};

export function DurableRecoveryActions({
  workloadId,
  projection,
  actions,
  onSubmitted,
}: Props) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [ticketReference, setTicketReference] = useState("");
  const [justification, setJustification] = useState(
    "Review provider evidence without resending the ambiguous delivery.",
  );
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const canAssessUnknown =
    projection.reconciliationFinding === "outcome_unknown";

  async function submitDryRun() {
    if (
      !canAssessUnknown ||
      !ticketReference.trim() ||
      justification.trim().length < 20
    ) {
      setMessage(
        "Add a ticket reference and at least 20 characters of justification.",
      );
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const actionId = `crm-${globalThis.crypto?.randomUUID?.() ?? Date.now()}`;
      const result = await dataProvider.createDurableEchoRecoveryAction(
        workloadId,
        {
          action_id: actionId,
          action_kind: "mark_outcome_unknown",
          finding: "outcome_unknown",
          reason_code: "provider_evidence_review",
          justification: justification.trim(),
          ticket_reference: ticketReference.trim(),
          expected_cost_cents: 0,
          expected_aggregate_version: projection.aggregateVersion,
          dry_run: true,
        },
      );
      const action: DurableRecoveryAction = {
        actionId,
        workloadId,
        attemptId: null,
        actionKind: "mark_outcome_unknown",
        finding: "outcome_unknown",
        actorId: "current operator",
        actorRole: "operator",
        reasonCode: "provider_evidence_review",
        ticketReference: ticketReference.trim(),
        expectedCostCents: 0,
        expectedAggregateVersion: projection.aggregateVersion,
        dryRun: true,
        createdAt: new Date().toISOString(),
        latestEvent: {
          sequence: 1,
          type: "dry_run_evaluated",
          outcome: String(result.status ?? "recorded"),
          result: null,
          createdAt: new Date().toISOString(),
        },
      };
      onSubmitted(action);
      setMessage("Dry-run request recorded in the durable audit ledger.");
    } catch {
      setMessage(
        "Recovery request was rejected; refresh the ledger and retry.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="space-y-3 rounded-md border bg-muted/20 p-3"
      aria-label="Durable recovery actions"
    >
      <div className="flex items-center gap-2">
        <ClipboardCheck className="size-4" />
        <h4 className="font-medium">Recovery audit</h4>
        <Badge variant="outline">dry-run only</Badge>
      </div>
      {canAssessUnknown ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            aria-label="Ticket reference"
            placeholder="Incident or ticket reference"
            value={ticketReference}
            onChange={(event) => setTicketReference(event.target.value)}
          />
          <Textarea
            aria-label="Recovery justification"
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            rows={2}
          />
          <Button
            type="button"
            variant="secondary"
            className="sm:col-span-2 sm:w-fit"
            disabled={submitting}
            onClick={submitDryRun}
          >
            {submitting ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : null}
            Assess outcome_unknown safely
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No supported dry-run action is available for the current finding.
        </p>
      )}
      {message ? (
        <p className="text-xs text-muted-foreground">{message}</p>
      ) : null}
      {actions.length ? (
        <div className="space-y-1 text-xs">
          <p className="text-muted-foreground">Recent audited requests</p>
          {actions.slice(0, 5).map((action) => (
            <div
              className="flex flex-wrap items-center gap-2"
              key={action.actionId}
            >
              <Badge variant="secondary">{action.actionKind}</Badge>
              <span>{action.latestEvent?.outcome ?? "recorded"}</span>
              <span className="font-mono text-muted-foreground">
                v{action.expectedAggregateVersion}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
